import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { formatAmount } from "@/utils/formatAmount";
import { useAllJvInvoices } from "@/features/finances/hooks/useFinances";

const JV_STATUS_COLOR: Record<string, "gray" | "blue" | "orange" | "green" | "red" | "yellow"> = {
  draft: "yellow", pending: "gray", approved: "blue", transfer_prepared: "orange", paid: "green", rejected: "red",
};

/**
 * Read-only overview of joint-venture partner cost invoices (consortium hub).
 * Decisions (approve/pay/book) happen in the "Faktury kosztowe" (/expenses) sections,
 * same as any other cost invoice. Each row links to its parent PRZ invoice.
 */
export function JvInvoicesPanel() {
  const { data: jvInvoices = [], isLoading } = useAllJvInvoices();

  return (
    <section>
      <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">Faktury partnerów JV (konsorcjum)</h2>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
        Przegląd. Akceptacja / płatność / księgowanie — w module{" "}
        <Link to="/expenses" className="text-[var(--color-primary)] hover:underline">Faktury kosztowe</Link>.
      </p>
      {isLoading ? (
        <p className="text-sm text-gray-400">Ładowanie…</p>
      ) : jvInvoices.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Brak faktur partnerów.</p>
      ) : (
        <div className="bg-white dark:bg-gray-800 overflow-x-auto border border-gray-100 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400">
              <tr>
                <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest">Partner</th>
                <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest">Nr FV</th>
                <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest">Netto</th>
                <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest">Brutto</th>
                <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest">Status</th>
                <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest">FV PRZ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
              {jvInvoices.map((jv) => (
                <tr key={jv.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                    {jv.resolved_partner_name}
                    {jv.consortium_agreement_detail?.partner_bank_account && (
                      <span className="block text-[10px] text-gray-400">{jv.consortium_agreement_detail.partner_bank_account}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-gray-500 dark:text-gray-400">{jv.invoice_number || "—"}</td>
                  <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">{formatAmount(jv.amount_net)}</td>
                  <td className="px-4 py-2 text-right text-gray-500 dark:text-gray-400">{formatAmount(jv.amount_gross)}</td>
                  <td className="px-4 py-2"><Badge color={JV_STATUS_COLOR[jv.status]}>{jv.status_display}</Badge></td>
                  <td className="px-4 py-2">
                    <Link
                      to={`/finances/invoices-prz/${jv.client_invoice}`}
                      className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline text-xs"
                    >
                      <ExternalLink size={12} /> otwórz
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
